import {
  resolveBrowserHref,
  type BrowserRef,
} from 'volto-ministerio-publico-federal/components/View/CapaView/helpers';

export type GuiaOrigem = 'governo-federal' | 'guia-mpf' | 'pagina-mpf';

export const GUIA_ICONES = [
  'IdentificationCard',
  'FileText',
  'BookOpen',
  'Certificate',
  'Info',
  'Question',
  'ShieldCheck',
] as const;

export type GuiaIcone = (typeof GUIA_ICONES)[number];

export const GUIA_ORIGENS: Record<GuiaOrigem, string> = {
  'governo-federal': 'Governo Federal',
  'guia-mpf': 'Guia do MPF',
  'pagina-mpf': 'Página do MPF',
};

const ICONE_POR_ORIGEM: Record<GuiaOrigem, GuiaIcone> = {
  'governo-federal': 'IdentificationCard',
  'guia-mpf': 'FileText',
  'pagina-mpf': 'BookOpen',
};

export const GUIAS_TITULO = 'Prefere ler? Tutoriais e guias por escrito';

export const GUIAS_BOTAO_ROTULO = 'Acessar';

export const GUIAS_MAX_ITENS = 6;

export const GUIA_EXTERNO_HINT = ' (abre em outro site)';

export interface GuiaItem {
  '@id'?: string;
  origem?: GuiaOrigem;
  icone?: GuiaIcone;
  titulo?: string;
  descricao?: string;
  href?: BrowserRef[];
  botaoRotulo?: string;
  externo?: boolean;
}

export interface GuiasData {
  '@type'?: string;
  titulo?: string;
  itens?: GuiaItem[];
}

export interface GuiaCard {
  key: string;
  sourceIndex: number;
  selo: string;
  icone: GuiaIcone;
  titulo: string;
  descricao: string;
  href?: string;
  botaoRotulo: string;
  externo: boolean;
}

const isOrigem = (valor?: string): valor is GuiaOrigem =>
  Boolean(valor && valor in GUIA_ORIGENS);

const isIcone = (valor?: string): valor is GuiaIcone =>
  (GUIA_ICONES as readonly string[]).includes(valor ?? '');

export const seloDoGuia = (origem?: GuiaOrigem, externo?: boolean): string => {
  const base = isOrigem(origem) ? GUIA_ORIGENS[origem] : '';
  return [base, externo ? 'site externo' : ''].filter(Boolean).join(' · ');
};

export const toGuiaCards = (itens?: GuiaItem[]): GuiaCard[] =>
  (itens ?? []).slice(0, GUIAS_MAX_ITENS).map((item, index) => {
    const origem = isOrigem(item?.origem) ? item.origem : 'pagina-mpf';
    return {
      key: item?.['@id'] ?? `guia-${index}`,
      sourceIndex: index,
      selo: seloDoGuia(item?.origem, item?.externo),
      icone: isIcone(item?.icone) ? item.icone : ICONE_POR_ORIGEM[origem],
      titulo: item?.titulo?.trim() ?? '',
      descricao: item?.descricao?.trim() ?? '',
      href: resolveBrowserHref(item?.href),
      botaoRotulo: item?.botaoRotulo?.trim() || GUIAS_BOTAO_ROTULO,
      externo: Boolean(item?.externo),
    };
  });

export const guiaIsCompleto = (card: GuiaCard): boolean =>
  Boolean(card.titulo && card.href);
